import avatarHappy from '@/assets/images/home/hero-avatar-happy.webp';
import avatarJoe from '@/assets/images/home/hero-avatar-joe.webp';
import standardGoogleIcon from '@/assets/images/home/standard-google.svg';
import standardLicensedIcon from '@/assets/images/home/standard-licensed.svg';
import standardWarrantyIcon from '@/assets/images/home/standard-warranty.svg';
import standardYearsIcon from '@/assets/images/home/standard-years.svg';
import stepAddressIcon from '@/assets/images/home/step-address.svg';
import stepMakeYoursIcon from '@/assets/images/home/step-make-yours.svg';
import stepProjectIcon from '@/assets/images/home/step-project.svg';
import stepProsIcon from '@/assets/images/home/step-pros.svg';

export const HOME_NAVBAR = {
  label: 'Main',
  logoAlt: 'Direct Exteriors',
  openMenuLabel: 'Open menu',
  closeMenuLabel: 'Close menu',
  links: [
    { title: 'Why Direct Exteriors', id: 'why' },
    { title: 'How it Works', id: 'how-it-works' },
    { title: 'See Price', id: 'see-price' },
    { title: 'Who Installs', id: 'who-installs' },
    { title: 'FAQs', id: 'faq' },
  ],
  blogLink: { title: 'Blog', to: '/blog' },
};

export const HOME_HERO = {
  title: 'The Easiest, Most Affordable Way to Buy a Roof.',
  description:
    'Get measurements, choose your materials, and see your actual pricing — all without waiting for an estimate or sitting through a sales pitch.',
  note: 'No sales appointments. Satisfaction guaranteed.',
  cta: 'Get My Roof Price',
  imageAlt: 'Homeowner checking her roof price on a phone',
  addressLabel: 'Address',
  rooferLabel: 'Selected Roofer',
  // Listed bottom card first so the selected one renders on top
  roofers: [
    { name: 'Happy Roofer Co.', avatar: avatarHappy, rating: 4, selected: false },
    { name: 'Joe’s Roofing Inc.', avatar: avatarJoe, rating: 5, selected: true },
  ],
  savings: { label: 'Save', qualifier: 'up to', amount: '20%' },
};

export const HOME_WHY = {
  eyebrow: 'Why Direct Exteriors',
  title: 'Skip the Runaround and Added Costs',
  intro:
    'Traditional roofing makes getting a price a long, expensive process. Direct Exteriors uses satellite roof measurement technology to quickly plan your project, then lets you explore materials and pricing online.',
  closing: [
    'By putting the essential parts of the process online, we make buying a roof easier—',
    'and eliminate unnecessary costs along the way.',
  ],
  comparisons: [
    {
      title: 'Traditional Roofing',
      highlighted: false,
      steps: [
        { text: 'Schedule an estimate' },
        { text: 'Wait for an appointment' },
        { text: 'Sit through a sales presentation' },
        { text: 'Do it all over again', strong: true },
        { text: 'Compare different quotes' },
        { text: 'Follow up and wait for responses' },
        { text: 'Negotiate and hope for the best' },
      ],
    },
    {
      title: 'Direct Exteriors',
      highlighted: true,
      steps: [
        { text: 'Enter your address' },
        { text: 'Get satellite measurements' },
        { text: 'Explore materials online' },
        { text: 'See your actual roof price' },
        { text: 'Explore payment options' },
        { text: 'Move forward when you’re ready' },
        { text: 'Pay a lower price', strong: true },
      ],
    },
  ],
  cta: 'See My Roof Price',
};

export const HOME_HOW_IT_WORKS = {
  eyebrow: 'How it Works',
  title: 'Four Easy Steps. No Sales Pitch or Extra Costs Required.',
  steps: [
    {
      title: 'Start With Your Address',
      text: 'Tell us where you live and a little about your home.',
      icon: stepAddressIcon,
    },
    {
      title: 'See Your\nProject',
      text: 'Roof data and measurement technology help us determine what your project requires.',
      icon: stepProjectIcon,
    },
    {
      title: 'Make it\nYours',
      text: 'Review pricing, choose materials, and explore payment options online.',
      icon: stepMakeYoursIcon,
    },
    {
      title: 'Leave it to\nthe Pros',
      text: 'Your project is completed by a qualified roofing professional from the Direct Exteriors contractor network.',
      icon: stepProsIcon,
    },
  ],
  cta: 'Get Started',
};

export const HOME_VIDEO = {
  title: 'How it Works',
  // TODO: replace with Joel's revised video once it's ready
  src: '/video.mp4',
  playLabel: 'Play the How it Works video',
};

export const HOME_SEE_PRICE = {
  eyebrow: 'Pay for What Matters',
  title: 'Cut the Sales Costs.\nNot the Quality.',
  description:
    'The traditional sales process involves paying salespeople and commissions, adding unnecessary costs. Direct Exteriors cuts those unnecessary costs by making the buying process smarter. More of your budget goes toward materials, installation, and protecting your home. Not a salesperson’s commission check.',
  benefits: [
    'No salesperson commission.',
    'No unnecessary sales process.',
    'A smarter path to a new roof.',
  ],
  cta: 'Get My Roof Price',
  imageAlt: 'House with a new roof, measured with satellite data',
};

export const HOME_WHO_INSTALLS = {
  eyebrow: 'Who Installs Your Roof?',
  title: 'Lower Prices, Higher Standards',
  description:
    'Direct Exteriors makes it easy to buy a roof online. Our standards for the professionals who install them are anything but relaxed. Here are just some of the standards all our roofing contractors meet.',
  standards: [
    {
      title: '5+ Years in Business',
      text: 'Contractors must have at least five consecutive years in business, demonstrating the experience and stability to take on your project.',
      icon: standardYearsIcon,
    },
    {
      title: '4.7+ Google Rating',
      text: 'With at least 100 customer reviews, contractors must demonstrate a strong track record of quality work and customer satisfaction.',
      icon: standardGoogleIcon,
    },
    {
      title: 'Class A Licensed & Insured',
      text: 'Contractors must hold a Class A contractor license and maintain workers’ compensation and at least $1 million per occurrence / $2 million aggregate in general liability coverage.',
      icon: standardLicensedIcon,
    },
    {
      title: '10-Year Workmanship Warranty',
      text: 'Every contractor must provide at least a 10-year workmanship warranty, acknowledge warranty service requests within one business day, and promptly address emergency water intrusion.',
      icon: standardWarrantyIcon,
    },
  ],
  previousLabel: 'Previous standards',
  nextLabel: 'Next standards',
};

export const HOME_FAQ = {
  eyebrow: 'Frequently Asked Questions',
  title: 'Before You Get Started.',
  // TODO: answers 2–7 are our draft until the client sends their own
  items: [
    {
      question: 'Is Direct Exteriors a roofing contractor?',
      answer:
        'Direct Exteriors provides the digital buying experience that helps you understand your project, review pricing and options, and move forward. Your roof is installed by a qualified professional within the Direct Exteriors contractor network.',
    },
    {
      question: 'Who actually installs my roof?',
      answer:
        'A Class A licensed and insured roofing contractor from the Direct Exteriors network, who completes your project from start to finish.',
    },
    {
      question: 'Do I need an in-home appointment to get pricing?',
      answer:
        'No. Your price is based on satellite roof measurements, so you can see it online without a sales visit. If the measurements can’t be completed automatically for your home, our team reviews your property and follows up with your price.',
    },
    {
      question: 'How is my roof measured?',
      answer:
        'We use satellite roof measurement technology to determine your roof’s size and pitch. Pricing assumes the roof is in good structural condition. If damaged decking or other hidden issues are found during installation, additional costs may apply.',
    },
    {
      question: 'How are contractors qualified?',
      answer:
        'Every contractor in our network has to meet our standards for experience, customer reviews, licensing and insurance, and workmanship warranty. You can find the specifics in the "Who Installs Your Roof?" section above.',
    },
    {
      question: 'Why not just get estimates from local roofers?',
      answer:
        'You can, and it’s always good to compare. With Direct Exteriors you see your price online without scheduling visits or sitting through sales presentations, and because there’s no salesperson commission, more of your budget goes toward materials and installation.',
    },
    {
      question: 'What happens after I submit my information?',
      answer:
        'We measure your roof and show your price, usually within a few minutes. You can then choose a material, add skylight replacement if needed, pick a preferred installation date and secure your job with a $500 refundable deposit. Our team follows up to confirm the details.',
    },
  ],
};

export const HOME_CTA = {
  eyebrow: 'Ready When You Are',
  title: 'Start With a Price,\nNot an Appointment.',
  description:
    'See what your roof project looks like, understand your options and take the first step online.',
  cta: 'Get My Roof Price',
  imageAlt: 'Smiling homeowner checking his roof price on a phone',
};

export const HOME_ADDRESS_ENTRY = {
  title: 'Get Your Roof Price',
  description:
    'Enter your address and we’ll measure your roof by satellite to show your actual price.',
  placeholder: 'Enter your home address',
  clearLabel: 'Clear address',
  submit: 'See My Roof Price',
  errors: {
    missingAddress: 'Please enter your address.',
    missingStreet: 'Please choose an address with a street from the list.',
    requestFailed: 'Something went wrong. Please try again.',
  },
};

export const HOME_FOOTER = {
  label: 'Footer',
  copyright: 'Direct Exteriors. All rights reserved.',
  links: [
    { title: 'Blog', to: '/blog' },
    { title: 'Terms & Conditions', to: '/terms-and-conditions' },
    { title: 'Privacy Policy', to: '/privacy-policy' },
  ],
};
